import { postJSON } from 'common/services/api'
import dayjs from 'dayjs'
import { useMutation } from 'react-query'
import {
  MEDICATION_SCHEDULE_PATTERN_DISCONTINUE_URL,
  MEDICATION_SCHEDULE_PATTERN_QUERY_KEY
} from './queryConstants'
import { useInvalidateMedicationQueries } from './useInvalidateMedicationQueries'

type UseMedicationSchedulePatternDiscontinueReturn = {
  discontinueMedicationSchedulePattern: (reasonDeleted: string) => Promise<void>
  isDiscontinuingMedicationSchedulePattern: boolean
}

export type UseMedicationSchedulePatternDiscontinueProps = {
  medicationId: string
  onSuccess?: () => void
  onError?: () => void
}

const useMedicationSchedulePatternDiscontinue = ({
  medicationId,
  onSuccess,
  onError
}: UseMedicationSchedulePatternDiscontinueProps): UseMedicationSchedulePatternDiscontinueReturn => {
  const invalidateMedicationQueries = useInvalidateMedicationQueries()

  const discontinueMedicationSchedulePatternFunction = async (
    reason: string
  ): Promise<unknown> => {
    return postJSON({
      url: MEDICATION_SCHEDULE_PATTERN_DISCONTINUE_URL.replace(
        '{id}',
        medicationId
      ),
      method: 'PUT',
      json: {
        reason,
        endDate: dayjs().toISOString()
      }
    })
  }

  const {
    mutateAsync: discontinueMedicationSchedulePatternMutation,
    isLoading: isDiscontinuingMedicationSchedulePattern
  } = useMutation<unknown, unknown, string, unknown>(
    [MEDICATION_SCHEDULE_PATTERN_QUERY_KEY],
    discontinueMedicationSchedulePatternFunction,
    {
      onSuccess: () => {
        invalidateMedicationQueries()
        onSuccess?.()
      },
      onError: () => {
        onError?.()
      }
    }
  )

  const discontinueMedicationSchedulePattern = async (
    reason: string
  ): Promise<void> => {
    await discontinueMedicationSchedulePatternMutation(reason)
  }

  return {
    discontinueMedicationSchedulePattern,
    isDiscontinuingMedicationSchedulePattern
  }
}

export default useMedicationSchedulePatternDiscontinue
